import { type JSX, useCallback } from 'react';

import { Skeleton } from '@plurall/elo-v3';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';

import { PageContent } from '@/components';
import {
  BenchmarkingHeader,
  BenchmarkingMecEmptyState,
  BenchmarkingProvider,
  BenchmarkingTabs,
  useResolvedSchool,
  type BenchmarkingUrlFilters,
  benchmarkingFiltersSchema,
} from '@/features/benchmarking';
import { AiAssistantButtonLabeledContext } from '@/features/performance-dashboard/components/AiAssistantButton/AiAssistantButtonLabeledContext';
import { requireAuthenticated } from '@/permissions';

import '../styles.scss';

export const Route = createFileRoute('/microdados')({
  staticData: { title: 'Microdados' },
  beforeLoad: requireAuthenticated(),
  validateSearch: (search: Record<string, unknown>): BenchmarkingUrlFilters => benchmarkingFiltersSchema.parse(search),
  component: BenchmarkingLayout,
});

function BenchmarkingLayout(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewRoute = location.pathname === '/microdados/pdf-preview';
  const { isLoading, isMecMissing, hasNoAccessibleSchool } = useResolvedSchool(search);

  const handleNavigate = useCallback(
    ({ search: nextSearch, replace }: { search: BenchmarkingUrlFilters; replace?: boolean }) => {
      void navigate({ to: '.', search: nextSearch, replace });
    },
    [navigate]
  );

  return (
    <BenchmarkingProvider
      search={search}
      navigate={handleNavigate}>
      <BenchmarkingHeader />
      <PageContent>
        {isLoading && !isPreviewRoute ? (
          <section
            className='dashboard-grid'
            aria-label='Verificando cadastro da escola no MEC'>
            <Skeleton
              className='full'
              width='100%'
              height='240px'
            />
          </section>
        ) : (isMecMissing || hasNoAccessibleSchool) && !isPreviewRoute ? (
          <BenchmarkingMecEmptyState />
        ) : (
          <AiAssistantButtonLabeledContext value>
            {!isPreviewRoute && <BenchmarkingTabs />}
            <Outlet />
          </AiAssistantButtonLabeledContext>
        )}
      </PageContent>
    </BenchmarkingProvider>
  );
}
